import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { ApiService } from '../api-service/api-service';
import { SessionStorageUtility } from '../../utils/sessionStorageUtils';
import { LocalStorageUtility } from '../../utils/localStorageUtils';
import { SESSION_STORAGE_KEYS } from '../../constants/storage.constant';
import { endPoints } from '../../constants/endpoints';

@Injectable({
  providedIn: 'root',
})
export class SymphonyIdService {
  private processedUids = new Set<string>();

  constructor(
    private apiService: ApiService,
    private router: Router,
  ) {}

  private validateUid(uid: string): boolean {
    return !!(
      uid &&
      uid.trim().length > 0 &&
      /^[a-zA-Z0-9\-_]{8,}$/.test(uid.trim())
    );
  }

  handleUid(uid: string): Observable<{ success: boolean; data?: any }> {
    if (!this.validateUid(uid)) {
      return of({ success: false, data: null });
    }

    const normalizedUid = uid.trim();
    if (this.processedUids.has(normalizedUid)) {
      const cachedData = this.getSymphonyData();
      return of({ success: true, data: cachedData });
    }

    this.processedUids.add(normalizedUid);
    const endpoint = `${endPoints.casemgmt.getSymphonyData.URI}?uid=${normalizedUid}`;
    return this.apiService.callGetApi<any>(endpoint).pipe(
      map((response) => {
        if (response && typeof response === 'object') {
          this.storeSymphonyData(response);
          return { success: true, data: response };
        }
        return { success: false, data: null };
      }),
      catchError(() => {
        this.processedUids.delete(normalizedUid);
        return of({ success: false, data: null });
      }),
    );
  }

  processUidAsync(uid: string): void {
    if (!this.validateUid(uid) || this.processedUids.has(uid.trim())) {
      return;
    }

    this.handleUid(uid).subscribe({
      next: () => {
        // UID processing completed
      },
    });
  }

  storeSymphonyData(data: any): void {
    try {
      if (data && typeof data === 'object') {
        SessionStorageUtility.set(SESSION_STORAGE_KEYS.symphonyData, data);

        if (data.sessionData) {
          const sessionData = data.sessionData;
          Object.keys(sessionData).forEach((key) => {
            const value = sessionData[key];
            if (value !== undefined && value !== null) {
              SessionStorageUtility.set(key, value);
            }
          });
        }

        if (data.localData) {
          const localData = data.localData;
          Object.keys(localData).forEach((key) => {
            const value = localData[key];
            if (value !== undefined && value !== null) {
              LocalStorageUtility.set(key, value);
            }
          });
        }
      }
    } catch {
      // Silently handle errors
    }
  }

  getSymphonyData(): any {
    try {
      return SessionStorageUtility.get(SESSION_STORAGE_KEYS.symphonyData);
    } catch {
      return null;
    }
  }

  getSessionDataValue(key: string): any {
    try {
      const symphonyData = this.getSymphonyData();
      return symphonyData?.sessionData?.[key] || null;
    } catch {
      return null;
    }
  }

  getCaseData(): any {
    return this.getSessionDataValue('caseData');
  }

  removeUidFromUrl(): void {
    try {
      const urlTree = this.router.parseUrl(this.router.url);
      delete urlTree.queryParams['uid'];

      this.router.navigateByUrl(urlTree, {
        replaceUrl: true,
      });
    } catch {
      // Silently handle errors
    }
  }

  clearSymphonyData(): void {
    try {
      SessionStorageUtility.clearItem(SESSION_STORAGE_KEYS.symphonyData);
      this.processedUids.clear();
    } catch {
      // Silently handle errors
    }
  }

  isSymphonyDataAvailable(): boolean {
    try {
      const symphonyData = SessionStorageUtility.get(
        SESSION_STORAGE_KEYS.symphonyData,
      );
      const hasData = !!(symphonyData && symphonyData.sessionData);
      return hasData;
    } catch {
      return false;
    }
  }

  handleSymphonyId(
    symphonyId: string,
  ): Observable<{ success: boolean; data?: any }> {
    return this.handleUid(symphonyId);
  }

  processSymphonyIdAsync(symphonyId: string): void {
    this.processUidAsync(symphonyId);
  }

  removeSymphonyIdFromUrl(): void {
    this.removeUidFromUrl();
  }
}
